import axios from 'axios';

// Same-origin requests in production. In dev, Vite is on :5173 and Laravel on :8000.
// We need explicit baseURL only in dev.
const isDev = import.meta.env.DEV;
const apiBase = isDev ? 'http://localhost:8000' : '';

export const api = axios.create({
  baseURL: apiBase,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Ensure CSRF cookie is fetched before any state-changing request.
let csrfPromise = null;
export function ensureCsrf() {
  if (!csrfPromise) {
    csrfPromise = api.get('/sanctum/csrf-cookie').catch((err) => {
      csrfPromise = null;
      throw err;
    });
  }
  return csrfPromise;
}

export async function login(email, password, remember = false) {
  await ensureCsrf();
  const { data } = await api.post('/api/login', { email, password, remember });
  return data.user;
}

// Login B2C via OTP WhatsApp
export async function requestWhatsappOtp(phone) {
  await ensureCsrf();
  const { data } = await api.post('/api/auth/whatsapp/request-otp', { phone });
  return data;
}
export async function verifyWhatsappOtp(phone, code) {
  await ensureCsrf();
  const { data } = await api.post('/api/auth/whatsapp/verify-otp', { phone, code });
  return data;
}

export async function logout() {
  await ensureCsrf();
  const { data } = await api.post('/api/logout');
  csrfPromise = null;
  return data;
}

export async function fetchUser() {
  const { data } = await api.get('/api/user');
  return data;
}

export async function fetchImmobili(params = {}) {
  const { data } = await api.get('/api/immobili', { params });
  return data;
}

export async function fetchImmobile(id) {
  const { data } = await api.get(`/api/immobili/${id}`);
  return data.data;
}

// Dettaglio immobile + i suoi lead Getrix (org-scoped)
export async function fetchImmobileDetail(id) {
  const { data } = await api.get(`/api/immobili/${id}`);
  return { immobile: data.data, leads: data.leads || [], reportAccess: data.report_access || null };
}

// Lead/richieste Getrix dell'org (direzione lead → immobile)
export async function fetchGetrixLeads(params = {}) {
  const { data } = await api.get('/api/getrix-leads', { params });
  return data;
}
export async function fetchGetrixLead(id) {
  const { data } = await api.get(`/api/getrix-leads/${id}`);
  return data.data;
}

export async function fetchStats() {
  const { data } = await api.get('/api/immobili/stats');
  return data;
}

export async function fetchMappa(params = {}) {
  const { data } = await api.get('/api/immobili/mappa', { params });
  return data;
}

export async function fetchFilterOptions() {
  const { data } = await api.get('/api/immobili/filter-options');
  return data;
}

// === Lead (multi-tenant per organization) ===

export async function fetchLeads(params = {}) {
  const { data } = await api.get('/api/leads', { params });
  return data;
}

export async function fetchLead(id) {
  const { data } = await api.get(`/api/leads/${id}`);
  return data.data;
}

export async function fetchLeadStats(params = {}) {
  const { data } = await api.get('/api/leads/stats', { params });
  return data;
}

export async function createLead(payload) {
  await ensureCsrf();
  const { data } = await api.post('/api/leads', payload);
  return data.data;
}

export async function updateLead(id, payload) {
  await ensureCsrf();
  const { data } = await api.put(`/api/leads/${id}`, payload);
  return data.data;
}

export async function deleteLead(id) {
  await ensureCsrf();
  const { data } = await api.delete(`/api/leads/${id}`);
  return data;
}

// === Matches (Lead ↔ Immobile, per-organization) ===

export async function fetchMatches(params = {}) {
  const { data } = await api.get('/api/matches', { params });
  return data;
}

export async function fetchMatchStats() {
  const { data } = await api.get('/api/matches/stats');
  return data;
}

export async function createMatch(payload) {
  await ensureCsrf();
  const { data } = await api.post('/api/matches', payload);
  return data.data;
}

export async function updateMatch(id, payload) {
  await ensureCsrf();
  const { data } = await api.put(`/api/matches/${id}`, payload);
  return data.data;
}

export async function deleteMatch(id) {
  await ensureCsrf();
  const { data } = await api.delete(`/api/matches/${id}`);
  return data;
}

export async function suggestMatches(leadId) {
  await ensureCsrf();
  const { data } = await api.post(`/api/leads/${leadId}/suggest-matches`);
  return data;
}

// === WhatsApp (per-organization session via Baileys) ===

export async function whatsappStatus() {
  const { data } = await api.get('/api/whatsapp/status');
  return data;
}

export async function whatsappConnect() {
  await ensureCsrf();
  const { data } = await api.post('/api/whatsapp/connect');
  return data;
}

export async function whatsappDisconnect() {
  await ensureCsrf();
  const { data } = await api.post('/api/whatsapp/disconnect');
  return data;
}

export async function whatsappSend(to, message) {
  await ensureCsrf();
  const { data } = await api.post('/api/whatsapp/send', { to, message });
  return data;
}

export async function togglePreferito(id) {
  await ensureCsrf();
  const { data } = await api.post(`/api/immobili/${id}/toggle-preferito`);
  return data;
}

export async function toggleScarta(id) {
  await ensureCsrf();
  const { data } = await api.post(`/api/immobili/${id}/toggle-scarta`);
  return data;
}

export async function startAstemaster(id, { force = false } = {}) {
  await ensureCsrf();
  const url = `/api/immobili/${id}/astemaster/start` + (force ? '?force=1' : '');
  const { data } = await api.post(url);
  return data;
}

/**
 * Crea (o riusa) il link condivisibile al report di un immobile.
 * Non consuma il cap: condividere è un invito, non un'analisi.
 */
export async function creaLinkCondivisione(id) {
  await ensureCsrf();
  const { data } = await api.post(`/api/immobili/${id}/share`);
  return data;
}

export async function fetchOmiReports(immobileId) {
  const { data } = await api.get(`/api/immobili/${immobileId}/omi-reports`);
  return data.data || [];
}

export async function deleteOmiReport(id) {
  await ensureCsrf();
  const { data } = await api.delete(`/api/omi-reports/${id}`);
  return data;
}

export async function generateOmiReport(id, { raggio = 500, anni = null, tipologia = 'RES,MIX' } = {}) {
  await ensureCsrf();
  const body = { raggio, tipologia };
  if (anni && anni.length) body.anni = anni;
  // Timeout lungo lato client: lo script AdE può richiedere 30s-5min
  const { data } = await api.post(`/api/immobili/${id}/omi-report`, body, { timeout: 320000 });
  return data;
}

export async function sendLeadAccessLink(id) {
  await ensureCsrf();
  const { data } = await api.post(`/api/leads/${id}/send-access-link`);
  return data;
}

// Radar prospect (criteri ricerca personalizzati)
export async function fetchProspectRadar() {
  const { data } = await api.get('/api/me/radar');
  return data;
}
export async function saveProspectRadar(criteria) {
  await ensureCsrf();
  const { data } = await api.put('/api/me/radar', { criteria });
  return data;
}

// --- Ricerche salvate prospect (Home B2C) ---
export async function fetchSavedSearches() {
  const { data } = await api.get('/api/prospect/searches');
  return data.searches || [];
}
export async function createSavedSearch(payload) {
  await ensureCsrf();
  const { data } = await api.post('/api/prospect/searches', payload);
  return data.search;
}
export async function updateSavedSearch(id, payload) {
  await ensureCsrf();
  const { data } = await api.patch(`/api/prospect/searches/${id}`, payload);
  return data.search;
}
export async function deleteSavedSearch(id) {
  await ensureCsrf();
  await api.delete(`/api/prospect/searches/${id}`);
}
export async function markSearchSeen(id) {
  await ensureCsrf();
  await api.post(`/api/prospect/searches/${id}/seen`);
}

export async function fetchPlaceBoundary(q) {
  const { data } = await api.get('/api/places/boundary', { params: { q } });
  return data.polygon || null;
}

export async function fetchRecentListings() {
  const { data } = await api.get('/api/prospect/recent-listings');
  return data.listings || [];
}
export async function fetchNearbyListings(lat, lng) {
  const { data } = await api.get('/api/prospect/nearby-listings', { params: { lat, lng } });
  return data.listings || [];
}
export async function fetchTutorialImmobile() {
  const { data } = await api.get('/api/prospect/tutorial-immobile');
  return data.immobile || null;
}
export async function fetchAnalyzedList() {
  const { data } = await api.get('/api/prospect/analyzed-list');
  return data.analyzed || [];
}

export async function fetchAstemasterQueue() {
  const { data } = await api.get('/api/astemaster/queue');
  return data;
}

export async function astemasterStatus(id) {
  const { data } = await api.get(`/api/immobili/${id}/astemaster/status`);
  return data;
}

export async function publishGetrix(id) {
  await ensureCsrf();
  const { data } = await api.post(`/api/immobili/${id}/getrix-publish`);
  return data;
}

// === Settings — credenziali Getrix dell'org dell'utente loggato ===
export async function getGetrixCredentialsStatus() {
  const { data } = await api.get('/api/me/organization/getrix-credentials');
  return data;
}
export async function setGetrixCredentials({ email, password }) {
  await ensureCsrf();
  const { data } = await api.put('/api/me/organization/getrix-credentials', { email, password });
  return data;
}
export async function clearGetrixCredentials() {
  await ensureCsrf();
  const { data } = await api.delete('/api/me/organization/getrix-credentials');
  return data;
}

export async function setGetrixPrompts({ residenziale, commerciale }) {
  await ensureCsrf();
  const { data } = await api.put('/api/me/organization/getrix-prompts', { residenziale, commerciale });
  return data;
}

export async function setGetrixVideo(url) {
  await ensureCsrf();
  const { data } = await api.put('/api/me/organization/getrix-video', { url });
  return data;
}
export async function uploadGetrixAsset(kind, file) {
  await ensureCsrf();
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const { data } = await api.post('/api/me/organization/getrix-asset', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
export async function deleteGetrixAsset(kind) {
  await ensureCsrf();
  const { data } = await api.delete('/api/me/organization/getrix-asset', { data: { kind } });
  return data;
}

export async function getAutopublishFilter() {
  const { data } = await api.get('/api/me/organization/getrix-autopublish-filter');
  return data;
}
export async function setAutopublishFilter(rules) {
  await ensureCsrf();
  const { data } = await api.put('/api/me/organization/getrix-autopublish-filter', { rules });
  return data;
}

// === Onboarding Getrix (Puppeteer worker macchina-a-stati) ===
export async function startGetrixOnboarding({ email, password }) {
  await ensureCsrf();
  const { data } = await api.post('/api/me/organization/getrix-onboarding/start', { email, password });
  return data;
}
export async function getGetrixOnboardingStatus() {
  const { data } = await api.get('/api/me/organization/getrix-onboarding/status');
  return data;
}
export async function sendGetrixOnboardingCommand({ command, otp }) {
  await ensureCsrf();
  const payload = { command };
  if (otp) payload.otp = otp;
  const { data } = await api.post('/api/me/organization/getrix-onboarding/command', payload);
  return data;
}
